import { Box, useTheme } from "@mui/material";

export interface SegmentedTabOption {
  value: string;
  label: string;
}

interface SegmentedTabsProps {
  value: string;
  onChange: (value: string) => void;
  options: SegmentedTabOption[];
}

/**
 * Segmented tab control matching the Figma "Call details" design: a left-aligned,
 * hug-content group where the unselected tabs form a white bordered strip (outer
 * corners rounded, 1px dividers) and the selected tab is a fully-rounded pill with a
 * blue border, light-blue fill, and blue text. Used for the zoom-call detail
 * Overview / Analysis / Transcript tabs.
 */
const SegmentedTabs = ({ value, onChange, options }: SegmentedTabsProps) => {
  const theme = useTheme();
  const p = theme.nawy.colors.primitives;

  return (
    <Box role="tablist" sx={{ display: "inline-flex", alignItems: "stretch" }}>
      {options.map((opt, i) => {
        const selected = opt.value === value;
        const isFirst = i === 0;
        const isLast = i === options.length - 1;
        return (
          <Box
            key={opt.value}
            component="button"
            role="tab"
            aria-selected={selected}
            onClick={() => onChange(opt.value)}
            sx={{
              appearance: "none",
              fontFamily: "inherit",
              cursor: "pointer",
              whiteSpace: "nowrap",
              px: "16px",
              py: "8px",
              fontSize: 14,
              lineHeight: "20px",
              position: "relative",
              // Adjacent 1px borders collapse into single dividers; the selected
              // pill sits above its neighbours' borders.
              ml: isFirst ? 0 : "-1px",
              zIndex: selected ? 1 : 0,
              transition: "color 0.12s, border-color 0.12s, background-color 0.12s",
              // Only the group's outer corners are rounded — inner edges (e.g. the
              // right side of a selected first tab) stay straight.
              borderRadius: 0,
              ...(isFirst && {
                borderTopLeftRadius: "10px",
                borderBottomLeftRadius: "10px",
              }),
              ...(isLast && {
                borderTopRightRadius: "10px",
                borderBottomRightRadius: "10px",
              }),
              ...(selected
                ? {
                    border: `1px solid ${p.blue[500]}`,
                    backgroundColor: p.blue[50],
                    color: p.blue[600],
                    fontWeight: 600,
                  }
                : {
                    border: `1px solid ${p.navy[100]}`,
                    backgroundColor: p.white[100],
                    color: p.navy[900],
                    fontWeight: 500,
                    "&:hover": { color: p.blue[600] },
                  }),
            }}
          >
            {opt.label}
          </Box>
        );
      })}
    </Box>
  );
};

export default SegmentedTabs;
